import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {DetectMethodChanges, isInstrumentExpired, isVoid} from '../../../utils';
import {TradingInstrumentsService} from '../../../trading-instruments/trading-instruments-service.interface';
import {TradingInstrumentDisplayNameService} from '../../../trading-instruments/trading-instrument-display-name.service';
import {TradingInstrumentKind} from '../../../trading-instruments/trading-instrument-kind.enum';

export interface UnderlyingPickerItem {
    ticker: string;
    displayName: string;
}

@Component({
    selector: 'ets-hg-underlying-picker',
    templateUrl: 'hg-underlying-picker.component.html',
    styleUrls: ['hg-underlying-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgUnderlyingPickerComponent {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService
    ) {
    }

    readonly window = window;

    visible = false;

    underlyings: UnderlyingPickerItem[] = [];

    private _resolver: (value: string | null) => void;

    @DetectMethodChanges()
    show(baseUnderlying: string): Promise<string | null> {
        this.underlyings = this.buildUnderlyingList(baseUnderlying);
        this.visible = true;

        setTimeout(() => this._changeDetector.detectChanges());

        return new Promise<string | null>((resolve) => {
            this._resolver = resolve;
        });
    }

    @DetectMethodChanges()
    onItemSelected(item: UnderlyingPickerItem) {
        if (this._resolver) {
            this._resolver(item.ticker);
        }
        this._resolver = null;
        this.visible = false;
    }

    @DetectMethodChanges()
    onHidden() {
        if (this._resolver) {
            this._resolver(null);
        }
        this._resolver = null;
        this.visible = false;
    }

    private buildUnderlyingList(baseUnderlying: string): UnderlyingPickerItem[] {
        const items: UnderlyingPickerItem[] = [];

        // Add base underlying (e.g., SPX)
        if (!isVoid(baseUnderlying)) {
            const displayName = this._displayNameService.getDisplayNameForTicker(baseUnderlying);
            items.push({ticker: baseUnderlying, displayName});
        }

        // Add available ES futures contracts (exclude expired)
        const allInstruments = this._tiService.getAllTradingInstruments();
        const esContracts = allInstruments
            .filter(x => x.kind === TradingInstrumentKind.Futures
                && x.ticker.startsWith('ES')
                && !isInstrumentExpired(x))
            .sort((a, b) => {
                if (a.expirationDate && b.expirationDate) {
                    return new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime();
                }
                return a.ticker.localeCompare(b.ticker);
            })
            .slice(0, 2);

        for (const contract of esContracts) {
            items.push({
                ticker: contract.ticker,
                displayName: contract.displayName || contract.ticker
            });
        }

        return items;
    }
}
